<template>
    <div class="gif-generator-container">
        <div class="tool-left">
            <div class="gif-generator-card">
                <h1 class="title">🖼️ 动图魔法师</h1>
                <p class="subtitle">将您的图片序列转换为流畅的 GIF 动画</p>

                <!-- 拖拽上传区域 -->
                <div
                        class="upload-area"
                        @dragover.prevent
                        @dragenter.prevent
                        @drop="handleDrop"
                        :class="{ 'drag-over': isDragging }"
                >
                    <div class="upload-content">
                        <svg class="upload-icon" viewBox="0 0 24 24" width="48" height="48">
                            <path fill="currentColor" d="M19.35,10.04C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.04C2.34,8.36 0,10.91 0,14A6,6 0 0,0 6,20H19A5,5 0 0,0 24,15C24,12.36 21.95,10.22 19.35,10.04M19,18H6A4,4 0 0,1 2,14C2,11.95 3.53,10.24 5.56,10.03L6.63,9.92L7.13,8.93C8.08,7.29 9.92,6.22 12,6.22C14.29,6.22 16.24,7.8 16.71,10.04L16.8,10.44L17.79,10.35C19.37,10.22 20.7,11.36 20.7,12.95V15A3,3 0 0,1 17.7,18Z" />
                        </svg>
                        <p class="upload-text">拖拽图片到此处或点击上传</p>
                        <p class="upload-hint">支持 JPG, PNG, WEBP 格式</p>
                        <input
                                type="file"
                                ref="fileInput"
                                multiple
                                accept="image/*"
                                @change="handleImageUpload"
                                class="file-input"
                        />
                        <button class="upload-button" @click="$refs.fileInput.click()">
                            选择图片
                        </button>
                    </div>
                </div>

                <!-- 图片预览 -->
                <div v-if="images.length" class="section">
                    <h2 class="section-title">📸 已选图片 ({{ images.length }} 张)</h2>
                    <div class="image-preview">
                        <div
                                v-for="(img, index) in images"
                                :key="index"
                                class="image-item"
                        >
                            <img :src="img.url" :alt="`Preview ${index}`" />
                            <button
                                    class="remove-btn"
                                    @click="removeImage(index)"
                                    title="移除图片"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                </div>

                <!-- 生成设置 -->
                <div v-if="images.length" class="section settings">
                    <h2 class="section-title">⚙️ 生成设置</h2>
                    <div class="settings-grid">
                        <div class="setting-item">
                            <label for="delay">帧间隔 (毫秒):</label>
                            <input
                                    id="delay"
                                    type="number"
                                    v-model.number="frameDelay"
                                    min="20"
                                    max="1000"
                                    step="10"
                            />
                        </div>
                        <div class="setting-item">
                            <label for="repeat">循环次数:</label>
                            <select id="repeat" v-model.number="repeatCount">
                                <option :value="0">无限循环</option>
                                <option :value="1">播放一次</option>
                                <option :value="2">播放两次</option>
                                <option :value="3">播放三次</option>
                            </select>
                        </div>
                        <div class="setting-item">
                            <label for="quality">质量:</label>
                            <select id="quality" v-model.number="quality">
                                <option :value="1">高质量 (慢)</option>
                                <option :value="10">标准质量</option>
                                <option :value="30">低质量 (快)</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- 生成按钮 -->
                <div class="action-area">
                    <button
                            @click="generateGif"
                            :disabled="isGenerating || images.length === 0"
                            class="generate-btn"
                            :class="{ 'generating': isGenerating }"
                    >
                        <span v-if="!isGenerating">✨ 生成 GIF</span>
                        <span v-else>⏳ 生成中... {{ progress }}%</span>
                    </button>

                    <button
                            v-if="images.length"
                            @click="clearAll"
                            class="clear-btn"
                    >
                        🗑️ 清空所有
                    </button>
                </div>

                <!-- GIF 预览 -->
                <div v-if="gifUrl" class="section gif-result">
                    <h2 class="section-title">🎉 生成成功!</h2>
                    <div class="gif-preview-container">
                        <img :src="gifUrl" alt="生成的GIF" class="gif-preview" />
                        <div class="gif-info">
                            <p><strong>尺寸:</strong> {{ gifDimensions.width }} x {{ gifDimensions.height }}</p>
                            <p><strong>大小:</strong> {{ gifSize }}</p>
                        </div>
                        <a :href="gifUrl" download="my_animation.gif" class="download-btn">
                            ⬇️ 下载 GIF
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- 内容说明区 -->
        <div class="tool-content">
            <h2>🎬 图片生成 GIF 动图工具</h2>

            <div class="tool-content-intro">
                <p>将多张静态图片合成为流畅的 GIF 动画。支持调整帧间隔、循环次数、图像质量，制作表情包、演示动画、产品展示图。</p>
            </div>

            <div class="content-section">
                <h3><span class="icon">✨</span> 功能特点</h3>
                <ul>
                    <li><strong>拖拽排序</strong>：自由调整图片顺序，实时预览动画效果</li>
                    <li><strong>参数可调</strong>：帧间隔 100ms-2000ms，循环次数 0-无限</li>
                    <li><strong>质量选择</strong>：1-30 级质量调节，平衡清晰度与文件大小</li>
                    <li><strong>实时预览</strong>：生成前可预览动画效果</li>
                </ul>
            </div>

            <div class="content-section">
                <h3><span class="icon">📝</span> 使用教程</h3>
                <ol>
                    <li>点击或拖拽上传多张图片（建议 2-20 张）</li>
                    <li>拖动图片调整播放顺序</li>
                    <li>设置帧间隔、循环次数、质量参数</li>
                    <li>点击「生成 GIF」并下载</li>
                </ol>
            </div>

            <div class="content-section">
                <h3><span class="icon">💡</span> 制作建议</h3>
                <div class="tips-grid">
                    <div class="tip-card">
                        <strong>😄 表情包</strong>
                        <p>帧间隔 200ms，循环无限</p>
                    </div>
                    <div class="tip-card">
                        <strong>📦 产品展示</strong>
                        <p>帧间隔 1000ms，循环 3-5 次</p>
                    </div>
                    <div class="tip-card">
                        <strong>📚 教程演示</strong>
                        <p>帧间隔 500ms，循环无限</p>
                    </div>
                </div>
            </div>

            <div class="privacy-box">
                <span class="icon">🔒</span>
                <p><strong>隐私说明</strong>：使用 gif.js 技术在浏览器本地合成，图片不会上传到服务器。</p>
            </div>
        </div>
    </div>
</template>

<script>
    import seoMixin from '../js/seo-mixin';

export default {
        name: 'GifGenerator',
        mixins: [seoMixin],
        data() {
            return {
                seoTitle: '图片生成GIF动图在线工具 - FreeToolBox',
                seoDescription: '免费在线图片合成GIF工具，将多张JPG、PNG、WebP图片合成为流畅GIF动画。支持帧间隔、循环次数、质量调节，浏览器本地处理。',
                seoKeywords: '图片生成gif,gif制作,图片合成动图,gif动画制作,在线gif',
                images: [],
                gifUrl: null,
                isGenerating: false,
                progress: 0,
                frameDelay: 200,
                repeatCount: 0, // 0 for infinite
                quality: 10,
                gifDimensions: { width: 0, height: 0 },
                gifSize: '0 KB',
                isDragging: false,
            };
        },
        methods: {
            handleImageUpload(event) {
                const files = Array.from(event.target.files);
                this.processFiles(files);
            },
            handleDrop(event) {
                event.preventDefault();
                this.isDragging = false;
                const files = Array.from(event.dataTransfer.files);
                this.processFiles(files);
            },
            processFiles(files) {
                const imageFiles = files.filter(file => file.type.startsWith('image/'));
                const newImages = imageFiles.map(file => ({
                    blob: file,
                    url: URL.createObjectURL(file),
                }));
                this.images = [...this.images, ...newImages];
            },
            removeImage(index) {
                const removedImage = this.images.splice(index, 1)[0];
                URL.revokeObjectURL(removedImage.url);
                if (this.images.length === 0) {
                    this.clearGif();
                }
            },
            clearAll() {
                this.images.forEach(img => URL.revokeObjectURL(img.url));
                this.images = [];
                this.clearGif();
            },
            clearGif() {
                if (this.gifUrl) {
                    URL.revokeObjectURL(this.gifUrl);
                    this.gifUrl = null;
                }
                this.gifDimensions = { width: 0, height: 0 };
                this.gifSize = '0 KB';
            },
            async generateGif() {
                if (this.images.length === 0) return;

                this.isGenerating = true;
                this.progress = 0;
                this.clearGif();

                // 确保 gif.js 已加载
                if (typeof window.GIF === 'undefined') {
                    alert('GIF 库未加载，请刷新页面重试。');
                    this.isGenerating = false;
                    return;
                }

                const gif = new window.GIF({
                    workers: 2,
                    quality: this.quality,
                    workerScript: '/gif.worker.js',
                    repeat: this.repeatCount,
                });

                // 计算最大尺寸以保持一致性
                let maxWidth = 0;
                let maxHeight = 0;
                const loadedImages = [];

                for (let i = 0; i < this.images.length; i++) {
                    const img = new Image();
                    img.src = this.images[i].url;
                    await new Promise(resolve => img.onload = resolve);
                    loadedImages.push(img);
                    maxWidth = Math.max(maxWidth, img.width);
                    maxHeight = Math.max(maxHeight, img.height);
                    this.progress = Math.round((i + 1) / this.images.length * 50);
                }

                // 添加帧
                for (let i = 0; i < loadedImages.length; i++) {
                    gif.addFrame(loadedImages[i], {
                        delay: this.frameDelay,
                        copy: true,
                    });
                    this.progress = 50 + Math.round((i + 1) / loadedImages.length * 50);
                }

                gif.on('finished', (blob) => {
                    this.gifUrl = URL.createObjectURL(blob);
                    this.gifDimensions = { width: maxWidth, height: maxHeight };
                    this.gifSize = this.formatFileSize(blob.size);
                    this.isGenerating = false;
                    this.progress = 100;
                });

                gif.render();
            },
            formatFileSize(bytes) {
                if (bytes === 0) return '0 Bytes';
                const k = 1024;
                const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                const i = Math.floor(Math.log(bytes) / Math.log(k));
                return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
            }
        },
        beforeDestroy() {
            this.images.forEach(img => URL.revokeObjectURL(img.url));
            if (this.gifUrl) URL.revokeObjectURL(this.gifUrl);
        },
    };
</script>

<style scoped>
    .gif-generator-container {
        min-height: 100vh;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        padding: 20px;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 40px;
    }

    .tool-left {
        flex: 1;
        display: flex;
        justify-content: center;
        max-width: 55%;
    }

    .gif-generator-card {
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(10px);
        border-radius: 20px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        padding: 30px;
        width: 100%;
        max-width: 800px;
        margin: 20px 0;
    }

    .title {
        text-align: center;
        font-size: 2.5rem;
        font-weight: 700;
        margin-bottom: 10px;
        background: linear-gradient(90deg, #667eea, #764ba2);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }

    .subtitle {
        text-align: center;
        color: #666;
        margin-bottom: 30px;
        font-size: 1.1rem;
    }

    .upload-area {
        border: 3px dashed #ccc;
        border-radius: 15px;
        padding: 40px 20px;
        text-align: center;
        transition: all 0.3s ease;
        cursor: pointer;
        background-color: #f9f9f9;
        margin-bottom: 30px;
    }

    .upload-area:hover,
    .upload-area.drag-over {
        border-color: #667eea;
        background-color: #f0f4ff;
    }

    .upload-content {
        position: relative;
    }

    .upload-icon {
        color: #667eea;
        margin-bottom: 15px;
    }

    .upload-text {
        font-size: 1.2rem;
        font-weight: 500;
        margin-bottom: 5px;
        color: #333;
    }

    .upload-hint {
        color: #999;
        margin-bottom: 20px;
    }

    .file-input {
        display: none;
    }

    .upload-button {
        background: linear-gradient(90deg, #667eea, #764ba2);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 50px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: transform 0.2s, box-shadow 0.2s;
    }

    .upload-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }

    .section {
        margin-bottom: 30px;
    }

    .section-title {
        font-size: 1.4rem;
        margin-bottom: 15px;
        color: #333;
        display: flex;
        align-items: center;
    }

    .section-title::before {
        content: '';
        display: inline-block;
        width: 8px;
        height: 8px;
        background: linear-gradient(90deg, #667eea, #764ba2);
        border-radius: 50%;
        margin-right: 10px;
    }

    .image-preview {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 15px;
    }

    .image-item {
        position: relative;
        border-radius: 10px;
        overflow: hidden;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        transition: transform 0.2s;
    }

    .image-item:hover {
        transform: scale(1.05);
    }

    .image-item img {
        width: 100%;
        height: 120px;
        object-fit: cover;
        display: block;
    }

    .remove-btn {
        position: absolute;
        top: 5px;
        right: 5px;
        background: rgba(255, 0, 0, 0.8);
        color: white;
        border: none;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        cursor: pointer;
        font-size: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s;
    }

    .image-item:hover .remove-btn {
        opacity: 1;
    }

    .settings-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 20px;
    }

    .setting-item {
        display: flex;
        flex-direction: column;
    }

    .setting-item label {
        margin-bottom: 5px;
        font-weight: 500;
        color: #555;
    }

    .setting-item input,
    .setting-item select {
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 1rem;
    }

    .action-area {
        display: flex;
        justify-content: center;
        gap: 20px;
        margin-bottom: 30px;
    }

    .generate-btn, .clear-btn {
        padding: 14px 28px;
        border: none;
        border-radius: 50px;
        font-size: 1.1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .generate-btn {
        background: linear-gradient(90deg, #667eea, #764ba2);
        color: white;
        box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
    }

    .generate-btn:hover:not(:disabled) {
        transform: translateY(-3px);
        box-shadow: 0 8px 20px rgba(102, 126, 234, 0.6);
    }

    .generate-btn:disabled {
        background: #ccc;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }

    .clear-btn {
        background: #f5f5f5;
        color: #666;
        border: 1px solid #ddd;
    }

    .clear-btn:hover {
        background: #e0e0e0;
    }

    .generating {
        background: linear-gradient(90deg, #764ba2, #667eea);
    }

    .gif-result {
        text-align: center;
    }

    .gif-preview-container {
        background: #f9f9f9;
        border-radius: 15px;
        padding: 20px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .gif-preview {
        max-width: 100%;
        border-radius: 10px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin-bottom: 20px;
    }

    .gif-info {
        margin-bottom: 20px;
        color: #666;
    }

    .gif-info p {
        margin: 5px 0;
    }

    .download-btn {
        display: inline-block;
        background: linear-gradient(90deg, #667eea, #764ba2);
        color: white;
        padding: 14px 28px;
        border-radius: 50px;
        text-decoration: none;
        font-weight: 600;
        font-size: 1.1rem;
        transition: all 0.3s ease;
        box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
    }

    .download-btn:hover {
        transform: translateY(-3px);
        box-shadow: 0 8px 20px rgba(102, 126, 234, 0.6);
    }

    @media (max-width: 768px) {
        .gif-generator-container {
            padding: 10px;
        }

        .gif-generator-card {
            padding: 20px;
            border-radius: 15px;
        }

        .title {
            font-size: 2rem;
        }

        .image-preview {
            grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
        }

        .image-item img {
            height: 100px;
        }

        .action-area {
            flex-direction: column;
            gap: 10px;
        }

        .generate-btn, .clear-btn {
            width: 100%;
        }
    }
    /* 内容说明区样式 */
    .tool-content {
        flex: 1;
        max-width: 45%;
        margin: 0;
        padding: 0 20px;
        text-align: left;
    }

    .tool-content h2 {
        font-size: 1.8em;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .tool-content h2::before {
        content: '';
        display: inline-block;
        width: 5px;
        height: 32px;
        background: linear-gradient(180deg, #ff9800, #ffc107);
        border-radius: 3px;
    }

    .tool-content-intro {
        background: linear-gradient(135deg, #fff8f0 0%, #fff3e0 100%);
        border-radius: 16px;
        padding: 25px 30px;
        margin-bottom: 30px;
        border: 1px solid #ffe0b2;
        box-shadow: 0 4px 15px rgba(255, 152, 0, 0.08);
    }

    .tool-content-intro p {
        color: #e65100;
        font-size: 15px;
        line-height: 1.9;
        margin: 0;
    }

    .content-section {
        background: #fff;
        border-radius: 16px;
        padding: 25px 30px;
        margin-bottom: 20px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        border: 1px solid #f0f0f0;
        transition: box-shadow 0.3s ease;
    }

    .content-section:hover {
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    }

    .content-section h3 {
        color: #333;
        font-size: 1.15em;
        margin: 0 0 15px 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .content-section h3 .icon {
        font-size: 1.2em;
    }

    .content-section ul, .content-section ol {
        margin: 0;
        padding-left: 24px;
    }

    .content-section li {
        color: #555;
        line-height: 2;
        margin-bottom: 8px;
        position: relative;
    }

    .content-section ul li::marker {
        color: #ff9800;
    }

    .content-section ol li::marker {
        color: #ff9800;
        font-weight: 600;
    }

    .content-section strong {
        color: #333;
    }

    .tips-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 15px;
    }

    .tip-card {
        background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
        border-radius: 12px;
        padding: 15px 18px;
        border: 1px solid #ffeeba;
    }

    .tip-card strong {
        color: #856404;
        display: block;
        margin-bottom: 6px;
    }

    .tip-card p {
        margin: 0;
        font-size: 13px;
        color: #856404;
    }

    .privacy-box {
        background: linear-gradient(135deg, #f0f9f0 0%, #e8f5e8 100%);
        border: 1px solid #c8e6c9;
        border-radius: 12px;
        padding: 18px 25px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    .privacy-box .icon {
        font-size: 1.5em;
        flex-shrink: 0;
    }

    .privacy-box p {
        margin: 0;
        color: #2e7d32;
        line-height: 1.7;
    }
</style>



